import { TFunction } from "i18next";
import moment from 'moment';
import { ProductState } from "src/api/customer";
import appState from "src/model/state";
import { getCountryLanguage, getCurrentCountry, getCurrentDomainForCookies, getCurrentLanguage, setZaraApiHost } from "./BasePathTools";
import { APP_USA_SHOP_ID, ROUTE_PATHS } from "./constants";
import { getEnv } from "./EnvironmentTools";
import { getAppVersion } from "./eventsBus";
import { getEntryOrigin } from "./init/configuration";
import { getCurrentAvailableLanguage } from "./Market";
import { ORIGIN_PARAM_NAME } from "src/constants/UrlConstants";
import { ENTRY_ORIGIN_VARIANTS } from "src/model/shop.model";

export interface ClassMap {
  [index: string]: boolean;
}

export function getClassnames(classMap: ClassMap): string {
  return Object.keys(classMap)
    .filter((className) => classMap[className])
    .join(" ");
}

export function getOvixiaTokenFromCookie() {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; token=`);
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  } else {
    return null;
  }
}

export function getStorePathData() {
  const value = `; ${document.cookie}`;
  const storePath = /; storepath=(.*?);/.exec(value);

  if (storePath) {
    const parcialPathToken = storePath[1];
    return decodeURIComponent(parcialPathToken);
  } else {
    return null;
  }
}

export function getGeoFromCookie(): string {
  const storePathComplete = getStorePathData();
  if (storePathComplete) {
    const isoCountry = storePathComplete.split("/");
    return isoCountry[0]?.toUpperCase();
  } else {
    return null;
  }
}

export function deleteOvixiaToken() {
  document.cookie = `token=; Domain=${getCurrentDomainForCookies()} path=/; Secure; SameSite=none; max-age=0; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
}


export function exists(status: ProductState) {
  return status !== "SOLD" && status !== "DELETED";
}

export function handleProductStatus(status: ProductState, changed?: boolean) {
  if (!exists(status)) {
    return "cart_error_unavailable";
  } else if (
    [
      "PAUSED",
      "PAUSED_PENDING_REVIEW",
      "IN_REVIEW",
      "PENDING_REVIEW",
      "AMANDA_PROCESSING",
      "AMANDA_PROCESSING_GRID",
      "PAUSED_AMANDA_PROCESSING",
      "PENDING_EVALUATION_AI",
      "PAUSED_PENDING_EVALUATION_AI",
      "REJECTED",
      "DISPUTED",
    ].find((s) => s === status)
  ) {
    return "cart_error_temporarily_unavailable";
  } else if (changed) {
    return "cart_article_changed";
  } else return null;
}


export const getStatusCodeFirstNumber = (statusCode: number | string) => {
  return Array.from(statusCode?.toString())?.[0];
};

export const getPascalCase = (text: string, splitted?: boolean) => {
  try {
    if (!splitted) {
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    } else {
      const textArray = text.split("/").map((item) => {
        const itemNew = item.trim();
        let textTranform = [];
        if (itemNew.includes("|")) {
          textTranform = itemNew.split("|").map((part) => {
            const partNew = part.trim();
            return (
              partNew.charAt(0).toUpperCase() + partNew.slice(1).toLowerCase()
            );
          });
          return textTranform.join(" | ");
        } else {
          return (
            itemNew.charAt(0).toUpperCase() + itemNew.slice(1).toLowerCase()
          );
        }
      });
      return textArray.join(" / ");
    }
  } catch {
    return text || "";
  }
};

export const getStaticImgSrc = (): string => {
  const baseUrl = getEnv().zaraStaticBaseUrl;
  return baseUrl;
};

export const isApp = (): boolean => {
  return isIOS() || isAndroid();
};

export const isIOS = (): boolean => {
  const userAgent = window.navigator.userAgent;
  return userAgent.includes("ZaraApp") && userAgent.includes("iOS");
};

export const isAndroid = (): boolean => {
  const userAgent = window.navigator.userAgent;
  return userAgent.includes("ZaraApp") && userAgent.includes("Android");
};

export const getAppEnviroment = () => {
  // userAgent android webview -> ZaraApp/13.11.0 Dalvik/2.1.0 (Linux; U; Android 10; Android SDK built for x86 Build/QSR1.190920.001) Zara/App/v13.11.0
  // userAgent iOS webview -> ZaraApp/13.11.0 (iPhone; iOS 16.0) (compatible; Zara/App/iOS/v13.11.0)
  const userAgent = window.navigator.userAgent;
  if (userAgent.includes("ZaraApp") && userAgent.includes("Android")) {
    return "androidWebView";
  } else if (userAgent.includes("ZaraApp") && userAgent.includes("iOS")) {
    return "iOSWebView";
  } else return null;
};

export function uint8ArrayToBase64(uint8Array: Uint8Array): string {
  let binary = "";
  const bytes = new Uint8Array(uint8Array);
  const len = bytes.byteLength;

  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export const getLegalsLink = (linkLegal: string, lang?: string): string => {
  const { store } = appState.get();

  const legalContent = {
    "T&C": store.legalContent.policies?.find((policy) =>  policy?.name === "general-terms-and-conditions")?.url,
    "P&P": store.legalContent.policies?.find((policy) =>  policy?.name === "general-privacy-policy")?.url,
    MC: store.legalContent.omnibusUrl,
    LD: store.legalContent.legalDescription,
    LT: store.legalContent.legalTitle,
    FT: store.legalContent.feedTitle,
    FD: store.legalContent.feedDescription,
    DP: store.legalContent.policies?.find((policy) =>  policy?.name === "usa-california-disclosure-policy")?.url,
    CP: store.legalContent.policies?.find((policy) =>  policy?.name === "usa-california-chain-policy")?.url,
  };
  return legalContent[linkLegal]?.replace(
    /-en_/g,
    "-" + getCurrentLanguage() + "_",
  );
};

export const isRGPDCompliant = (): boolean => {
  const { market } = appState.get();

  return market.rgpd?.isEnabled === true && market.rgpd?.showPopup === true;
};

export function formatShipmentCompletionRate(rate, translationText) {
  const percentage = rate * 100;
  const formattedRate = percentage % 1 === 0 
      ? percentage.toFixed(0) 
      : percentage.toFixed(2);
  
  return `${formattedRate}% ${translationText}`;
}
export function toCapitalizedLowerCase(value:string | null) {
  if (typeof value !== 'string' || value === null) return '';
  const lowerCaseValue = value.toLowerCase();
  return lowerCaseValue.charAt(0).toUpperCase() + lowerCaseValue.slice(1);
}
export const isResidentCountryUE = (countryCode: string): boolean => {
  const countryUE = [
    "DE",
    "BE",
    "HR",
    "DK",
    "ES",
    "FR",
    "IE",
    "LV",
    "LU",
    "NL",
    "SE",
    "BG",
    "SK",
    "EE",
    "GR",
    "MT",
    "PL",
    "CZ",
    "AT",
    "CY",
    "SI",
    "FI",
    "HU",
    "IT",
    "LT",
    "PT",
    "RO",
  ];
  return countryUE.includes(countryCode);
};

const getDateFormats = () => {
  const { market } = appState.get();
  const currentPathLanguage = getCurrentAvailableLanguage(market)
  return currentPathLanguage?.formats?.date ?? {shortDate: "d MMM y", longDate: "d MMM y H:mm:ss"}
}

const dateToLongormat = (parcialDate:string): string => {
  const parseDate = new Date(parcialDate)
  return Intl.DateTimeFormat(getCurrentLanguage(), {dateStyle: 'medium',timeStyle: 'short'}).format(parseDate)?.toString()
}

export const dateToShortFormat = (parcialDate: string): string => {
  const formats = getDateFormats()

  
  if(formats?.shortDate?.startsWith("d")){
    return moment(parcialDate).format("DD/MM/YYYY")
  } else {
    return moment(parcialDate).format("MM/DD/YYYY")
  }

};

export const getDeadlineDateString = (
  dateTimeString: string,
  t: TFunction<"translation", undefined, "translation">,
  orderDetailSale?: boolean,
): string => {
  const date = dateToLongormat(dateTimeString)

  if (orderDetailSale) {
    return `${t("my_sales_shipping_deadline_text_order_detail_sale", {
      date: date
    })}`;
  } else {
    return `${t("my_sales_shipping_deadline_text", {
      date: date,
    })}`;
  }
};


export const getDeadlineDateStringShort = (
  dateTimeString: string,
  t: TFunction<"translation", undefined, "translation">,
): string => {
  const date = dateToLongormat(dateTimeString)
    return `${t("my_sales_order_date_limit", {
      date: date,
    })}`;
  
};

export function formatLocale(): string {
  const currentCountry = getCurrentCountry();
  const currentLanguage = getCurrentLanguage();

  const lowerCasePart = `${currentLanguage.toLowerCase()}`;
  const upperCasePart = `${currentCountry.toUpperCase()}`;

  return `${lowerCasePart}_${upperCasePart}`;
}

export const isMarketWithoutOuth = (storeId: string): boolean => { //TODO: Eliminar este metodo cuando se implemente Oauth en todos los mercados aqui listados
  const marketWithoutOuth = [
    "11737", //Croacia
    "11706", //Eslovenia
    "11705", //Eslovaquia
    "11708", //Grecia
    "11707", //Finlandia
    "10704", //Italia
    "11711", //Luxemburgo
    "11703", //Belgica
  ];
  return marketWithoutOuth.includes(storeId);
};

export const closeResell = () => {
  return getAppEnviroment() ? 
  location.replace(setZaraApiHost(`${getCountryLanguage()}recom/close`)) : 
  location.replace(setZaraApiHost(`${getCountryLanguage()}user/account`))
}

export const isMarketUSA = () => {
  const { market } = appState.get();
  return market.marketId === APP_USA_SHOP_ID;
}

export const concatenateDynamicTextWithPrefix = (items: string[], prefix: string): string => {
  if (!items || items.length === 0) return "";

  const formattedItems = items.join(", ").replace(/, ([^,]*)$/, " y $1");
  return `${prefix} ${formattedItems}`;
};



function normalizeVersion(version: string | number | null | undefined): number[] {
  if (version === null || version === undefined) {
    return [0];
  }
  
  const versionStr = String(version);
  
  return versionStr.split('.')
    .map(part => {
      const num = parseInt(part, 10);
      return isNaN(num) ? 0 : num;
    });
}

export function compareVersions(
  versionA: string | number | null | undefined, 
  versionB: string | number | null | undefined
): number {
  const partsA = normalizeVersion(versionA);
  const partsB = normalizeVersion(versionB);
  
  const maxLength = Math.max(partsA.length, partsB.length);
  
  for (let i = 0; i < maxLength; i++) {
    const partA = i < partsA.length ? partsA[i] : 0;
    const partB = i < partsB.length ? partsB[i] : 0;
    
    if (partA > partB) return 1;
    if (partA < partB) return -1;
  }
  
  return 0; 
}

export function isAppVersionAtLeast(minVersion: string | number): boolean {
  const currentVersion = getAppVersion();
  return compareVersions(currentVersion, minVersion) >= 0;
}

export function isAppVersionLessThan(maxVersion: string | number): boolean {
  const currentVersion = getAppVersion();
  return compareVersions(currentVersion, maxVersion) < 0;
}

export function isAppVersionBetween(minVersion: string | number, maxVersion: string | number): boolean {
  return isAppVersionAtLeast(minVersion) && isAppVersionLessThan(maxVersion);
}
